/**
 * Campaign Details Modal
 * Shows detailed campaign metrics and user journeys with filters, sorting, and pagination
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  X,
  Users,
  Mail,
  MousePointer,
  Eye,
  Clock,
  DollarSign,
  ChevronDown,
  ChevronUp,
  Calendar,
  Target,
  Search,
  Filter,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { api } from '@services/api';
import { CustomAutocomplete } from '@components/analytics/CustomAutocomplete';

interface CampaignDetailsModalProps {
  campaign: {
    campaign_id: string;
    campaign_name: string;
    send_date: string;
    total_recipients: number;
    unique_opens: number;
    unique_clicks: number;
    open_rate: number;
    click_rate: number;
    click_to_open_rate: number;
    conversion_rate?: number;
    total_conversions?: number;
    total_revenue?: number;
  };
  companyId: string;
  dataSourceId: string;
  onClose: () => void;
}

interface UserJourney {
  email: string;
  userId: string;
  events: Array<{
    type: string;
    timestamp: string;
    count?: number;
  }>;
  firstEvent: string;
  lastEvent: string;
  totalOpens: number;
  totalClicks: number;
}

export const CampaignDetailsModal: React.FC<CampaignDetailsModalProps> = ({
  campaign,
  companyId,
  dataSourceId,
  onClose,
}) => {
  const [expandedJourney, setExpandedJourney] = useState<string | null>(null);
  
  // Filters
  const [emailFilter, setEmailFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  
  // Sorting
  const [sortBy, setSortBy] = useState<'email' | 'totalOpens' | 'totalClicks' | 'lastEvent'>('lastEvent');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Fetch campaign details
  const { data: detailsData, isLoading: loadingDetails } = useQuery({
    queryKey: ['campaign-details', campaign.campaign_id, companyId, dataSourceId],
    queryFn: async () => {
      const response = await api.get(`/api/v1/email-campaigns/${campaign.campaign_id}`, {
        params: { companyId, dataSourceId },
      });
      return response.data.data;
    },
  });

  // Fetch user journeys with filters, sorting, and pagination
  const { data: journeysData, isLoading: loadingJourneys } = useQuery({
    queryKey: [
      'campaign-journeys', 
      campaign.campaign_id, 
      companyId, 
      dataSourceId, 
      emailFilter, 
      eventTypeFilter,
      sortBy,
      sortOrder,
      currentPage,
      itemsPerPage
    ],
    queryFn: async () => {
      const params: any = { 
        companyId, 
        dataSourceId,
        limit: itemsPerPage,
        skip: (currentPage - 1) * itemsPerPage,
        sortBy,
        sortOrder,
      };
      
      if (emailFilter.trim()) {
        params.email = emailFilter.trim();
      }
      
      if (eventTypeFilter) {
        params.eventType = eventTypeFilter;
      }
      
      const response = await api.get(
        `/api/v1/email-campaigns/${campaign.campaign_id}/user-journeys`,
        { params }
      );
      return response.data.data;
    },
  });

  const userJourneys: UserJourney[] = journeysData?.journeys || [];
  const totalJourneys = journeysData?.totalJourneys || 0;
  const totalPages = journeysData?.totalPages || 1;
  const availableEventTypes = journeysData?.filters?.availableEventTypes || [];
  const availableEmails = journeysData?.filters?.availableEmails || [];

  // Handle sort toggle
  const handleSort = (column: typeof sortBy) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  // Handle filter changes
  const handleEmailFilterChange = (value: string) => {
    setEmailFilter(value);
    setCurrentPage(1);
  };

  const handleEventTypeFilterChange = (value: string) => {
    setEventTypeFilter(value);
    setCurrentPage(1);
  };

  // Handle pagination
  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  // Render sortable header
  const SortableHeader = ({ 
    column, 
    children 
  }: { 
    column: typeof sortBy; 
    children: React.ReactNode 
  }) => (
    <th 
      className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase cursor-pointer hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-2">
        {children}
        {sortBy === column && (
          <ArrowUpDown className={`w-3 h-3 ${sortOrder === 'desc' ? 'rotate-180' : ''}`} />
        )}
      </div>
    </th>
  );

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-secondary-900 rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-secondary-200 dark:border-secondary-800">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-secondary-900 dark:text-white flex items-center gap-2">
                <Mail className="w-6 h-6 text-primary-600" />
                {campaign.campaign_name}
              </h2>
              <p className="text-sm text-secondary-500 mt-1">{campaign.campaign_id}</p>
              <div className="flex items-center gap-4 mt-3">
                <span className="text-sm text-secondary-600 dark:text-secondary-400 flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Sent: {new Date(campaign.send_date).toLocaleDateString()}
                </span>
                <span className="text-sm text-secondary-600 dark:text-secondary-400 flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {campaign.total_recipients.toLocaleString()} recipients
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-secondary-100 dark:hover:bg-secondary-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Key Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 mb-2">
                <Eye className="w-4 h-4" />
                <span className="text-xs font-medium uppercase">Open Rate</span>
              </div>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                {campaign.open_rate.toFixed(1)}%
              </p>
              <p className="text-xs text-blue-600/70 dark:text-blue-400/70 mt-1">
                {campaign.unique_opens} unique opens
              </p>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-2">
                <MousePointer className="w-4 h-4" />
                <span className="text-xs font-medium uppercase">Click Rate</span>
              </div>
              <p className="text-2xl font-bold text-green-700 dark:text-green-300">
                {campaign.click_rate.toFixed(1)}%
              </p>
              <p className="text-xs text-green-600/70 dark:text-green-400/70 mt-1">
                {campaign.unique_clicks} unique clicks
              </p>
            </div>

            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
              <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400 mb-2">
                <Target className="w-4 h-4" />
                <span className="text-xs font-medium uppercase">CTOR</span>
              </div>
              <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                {campaign.click_to_open_rate.toFixed(1)}%
              </p>
              <p className="text-xs text-purple-600/70 dark:text-purple-400/70 mt-1">
                Click-to-open rate
              </p>
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
              <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 mb-2">
                <DollarSign className="w-4 h-4" />
                <span className="text-xs font-medium uppercase">Conv Rate</span>
              </div>
              <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                {detailsData?.conversionData?.conversionRate ? 
                  `${detailsData.conversionData.conversionRate.toFixed(1)}%` : 
                  'N/A'}
              </p>
              <p className="text-xs text-amber-600/70 dark:text-amber-400/70 mt-1">
                {detailsData?.conversionData?.totalConversions ? 
                  `${detailsData.conversionData.totalConversions} conversions` :
                  'No data'}
              </p>
            </div>
          </div>

          {/* User Journeys Table */}
          <div className="bg-white dark:bg-secondary-900 rounded-lg border border-secondary-200 dark:border-secondary-800">
            <div className="p-4 border-b border-secondary-200 dark:border-secondary-800">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-secondary-900 dark:text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary-600" />
                  User Journeys ({campaign.total_recipients} campaign recipients, {totalJourneys} total interactions)
                </h3>
              </div>

              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Email Filter with CustomAutocomplete */}
                <CustomAutocomplete
                  label="Filter by Email"
                  value={emailFilter}
                  onChange={handleEmailFilterChange}
                  options={availableEmails}
                  placeholder="Search email..."
                />

                {/* Event Type Filter with CustomAutocomplete */}
                <CustomAutocomplete
                  label="Filter by Event Type"
                  value={eventTypeFilter}
                  onChange={handleEventTypeFilterChange}
                  options={availableEventTypes}
                  placeholder="All Events"
                />
              </div>
            </div>

            {loadingJourneys ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-4 border-primary-600 border-t-transparent"></div>
                <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-2">
                  Loading user journeys...
                </p>
              </div>
            ) : userJourneys.length === 0 ? (
              <div className="p-8 text-center">
                <Users className="w-12 h-12 text-secondary-300 dark:text-secondary-700 mx-auto mb-3" />
                <p className="text-secondary-600 dark:text-secondary-400">
                  No user journeys found with the current filters.
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-secondary-50 dark:bg-secondary-800 sticky top-0">
                      <tr>
                        <SortableHeader column="email">User Email</SortableHeader>
                        <SortableHeader column="totalOpens">Opens</SortableHeader>
                        <SortableHeader column="totalClicks">Clicks</SortableHeader>
                        <SortableHeader column="lastEvent">Last Activity</SortableHeader>
                        <th className="px-4 py-3 text-center text-xs font-medium text-secondary-500 uppercase">
                          Details
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-200 dark:divide-secondary-800">
                      {userJourneys.map(journey => (
                        <>
                          <tr
                            key={journey.userId}
                            className="hover:bg-secondary-50 dark:hover:bg-secondary-800/50"
                          >
                            <td className="px-4 py-3 text-sm text-secondary-900 dark:text-white">
                              {journey.email}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-secondary-600">
                              {journey.totalOpens}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-secondary-600">
                              {journey.totalClicks}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-secondary-600">
                              {new Date(journey.lastEvent).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() =>
                                  setExpandedJourney(
                                    expandedJourney === journey.userId ? null : journey.userId
                                  )
                                }
                                className="text-primary-600 hover:text-primary-700"
                              >
                                {expandedJourney === journey.userId ? (
                                  <ChevronUp className="w-4 h-4 inline" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 inline" />
                                )}
                              </button>
                            </td>
                          </tr>
                          {expandedJourney === journey.userId && (
                            <tr>
                              <td colSpan={5} className="px-4 py-3 bg-secondary-50 dark:bg-secondary-800/50">
                                <div className="space-y-2">
                                  <p className="text-xs font-medium text-secondary-700 dark:text-secondary-300">
                                    Event Timeline:
                                  </p>
                                  {journey.events.map((event, i) => (
                                    <div key={i} className="flex items-center gap-3 text-xs">
                                      <span className="text-secondary-500">
                                        {new Date(event.timestamp).toLocaleString()}
                                      </span>
                                      <span
                                        className={`px-2 py-1 rounded-full font-medium ${
                                          event.type === 'open'
                                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                            : event.type === 'click'
                                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                            : 'bg-secondary-100 text-secondary-700 dark:bg-secondary-700 dark:text-secondary-300'
                                        }`}
                                      >
                                        {event.type}
                                        {event.count && event.count > 1 ? ` (${event.count}x)` : ''}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="p-4 border-t border-secondary-200 dark:border-secondary-800 flex items-center justify-between">
                    <div className="text-sm text-secondary-600 dark:text-secondary-400">
                      Page {currentPage} of {totalPages}
                      <span className="ml-2 text-xs">
                        (Showing {(currentPage - 1) * itemsPerPage + 1}-
                        {Math.min(currentPage * itemsPerPage, totalJourneys)} of {totalJourneys})
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-2 rounded-lg border border-secondary-300 dark:border-secondary-600 hover:bg-secondary-100 dark:hover:bg-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      
                      {/* Page Numbers */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => goToPage(pageNum)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              currentPage === pageNum
                                ? 'bg-primary-600 text-white'
                                : 'border border-secondary-300 dark:border-secondary-600 hover:bg-secondary-100 dark:hover:bg-secondary-700'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="p-2 rounded-lg border border-secondary-300 dark:border-secondary-600 hover:bg-secondary-100 dark:hover:bg-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-secondary-200 dark:border-secondary-800 bg-secondary-50 dark:bg-secondary-800">
          <div className="flex justify-end">
            <button onClick={onClose} className="btn btn-secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
